import { useState, useEffect } from "react";
import { CheckCircle, AlertTriangle, XCircle, Info, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";

interface ValidationResult {
  type: "error" | "warning" | "info" | "success";
  messageKey: string;
  messageVars?: Record<string, string>;
  fixId?: string;
}

interface DorkValidatorProps {
  query: string;
  onQueryChange?: (newQuery: string) => void;
}

const VALID_OPERATORS = [
  "site", "inurl", "intitle", "intext", "filetype", "ext", "cache",
  "link", "related", "info", "define", "stocks", "phonebook",
  "movie", "weather", "allintitle", "allinurl", "allintext",
  "before", "after", "daterange", "numrange", "OR", "AND"
];

type FixFunction = (query: string) => string;

const FIX_FUNCTIONS: Record<string, FixFunction> = {
  "fix-unbalanced-quotes": (query) => {
    const quoteCount = (query.match(/"/g) || []).length;
    if (quoteCount % 2 !== 0) return query + '"';
    return query;
  },
  "fix-unbalanced-parens-open": (query) => {
    const openCount = (query.match(/\(/g) || []).length;
    const closeCount = (query.match(/\)/g) || []).length;
    if (openCount > closeCount) return query + ")".repeat(openCount - closeCount);
    return query;
  },
  "fix-unbalanced-parens-close": (query) => {
    const openCount = (query.match(/\(/g) || []).length;
    const closeCount = (query.match(/\)/g) || []).length;
    if (closeCount > openCount) return "(".repeat(closeCount - openCount) + query;
    return query;
  },
  "fix-operator-space": (query) => {
    return query
      .replace(/site:\s+/g, "site:")
      .replace(/filetype:\s+/g, "filetype:")
      .replace(/inurl:\s+/g, "inurl:")
      .replace(/intitle:\s+/g, "intitle:")
      .replace(/intext:\s+/g, "intext:")
      .replace(/ext:\s+/g, "ext:");
  },
  "fix-add-filetype": (query) => {
    const extensionMatch = query.match(/\.([a-zA-Z]{2,4})(?:\s|$)/i);
    if (extensionMatch) {
      const ext = extensionMatch[1].toLowerCase();
      return `filetype:${ext} ${query.replace(extensionMatch[0], " ").trim()}`;
    }
    return query;
  },
};

export function DorkValidator({ query, onQueryChange }: DorkValidatorProps) {
  const { t } = useLocale();
  const [results, setResults] = useState<ValidationResult[]>([]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const validationResults: ValidationResult[] = [];

    const doubleQuotes = (query.match(/"/g) || []).length;
    if (doubleQuotes % 2 !== 0) {
      validationResults.push({ type: "error", messageKey: "validator.unbalanced_quotes", fixId: "fix-unbalanced-quotes" });
    }

    const openParens = (query.match(/\(/g) || []).length;
    const closeParens = (query.match(/\)/g) || []).length;
    if (openParens > closeParens) {
      validationResults.push({ type: "error", messageKey: "validator.missing_close_paren", fixId: "fix-unbalanced-parens-open" });
    } else if (closeParens > openParens) {
      validationResults.push({ type: "error", messageKey: "validator.missing_open_paren", fixId: "fix-unbalanced-parens-close" });
    }

    const operatorPattern = /([a-zA-Z]+):/g;
    let match;
    while ((match = operatorPattern.exec(query)) !== null) {
      const operator = match[1].toLowerCase();
      if (!VALID_OPERATORS.map(o => o.toLowerCase()).includes(operator)) {
        validationResults.push({ type: "warning", messageKey: "validator.unknown_operator", messageVars: { op: match[1] } });
      }
    }

    const emptyOperatorPattern = /([a-zA-Z]+):\s*(?=[a-zA-Z]+:|$)/g;
    while ((match = emptyOperatorPattern.exec(query)) !== null) {
      validationResults.push({ type: "error", messageKey: "validator.empty_operator", messageVars: { op: match[1] } });
    }

    if (query.includes("site: ") || query.includes("filetype: ") || query.includes("inurl: ") ||
        query.includes("intitle: ") || query.includes("intext: ") || query.includes("ext: ")) {
      validationResults.push({ type: "warning", messageKey: "validator.space_after_colon", fixId: "fix-operator-space" });
    }

    const extensionPattern = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|xml|json)(?:\s|$)/i;
    if (extensionPattern.test(query) && !query.toLowerCase().includes("filetype:") && !query.toLowerCase().includes("ext:")) {
      validationResults.push({ type: "info", messageKey: "validator.use_filetype", fixId: "fix-add-filetype" });
    }

    const urlPattern = /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.[a-zA-Z]{2,})/;
    const urlMatch = query.match(urlPattern);
    if (urlMatch && !query.toLowerCase().includes("site:")) {
      validationResults.push({ type: "info", messageKey: "validator.use_site", messageVars: { domain: urlMatch[1] } });
    }

    const orCount = (query.match(/\sOR\s/gi) || []).length;
    if (orCount > 5) {
      validationResults.push({ type: "warning", messageKey: "validator.many_or" });
    }

    if (query.match(/-[a-zA-Z]+:/)) {
      const negationMatch = query.match(/-([a-zA-Z]+):/);
      if (negationMatch) {
        validationResults.push({ type: "info", messageKey: "validator.negating", messageVars: { op: negationMatch[1] } });
      }
    }

    if (validationResults.length === 0 && query.trim().length > 0) {
      validationResults.push({ type: "success", messageKey: "validator.valid" });
    }

    setResults(validationResults);
  }, [query]);

  const handleFix = (fixId: string) => {
    if (!onQueryChange || !FIX_FUNCTIONS[fixId]) return;
    const fixedQuery = FIX_FUNCTIONS[fixId](query);
    onQueryChange(fixedQuery);
  };

  if (!query.trim() || results.length === 0) return null;

  const getIcon = (type: ValidationResult["type"]) => {
    switch (type) {
      case "error": return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
      case "warning": return <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0" />;
      case "info": return <Info className="w-4 h-4 text-blue-400 shrink-0" />;
      case "success": return <CheckCircle className="w-4 h-4 text-neon-green shrink-0" />;
    }
  };

  const getBgColor = (type: ValidationResult["type"]) => {
    switch (type) {
      case "error": return "bg-red-500/10 border-red-500/30";
      case "warning": return "bg-yellow-500/10 border-yellow-500/30";
      case "info": return "bg-blue-500/10 border-blue-500/30";
      case "success": return "bg-neon-green/10 border-neon-green/30";
    }
  };

  const hasErrors = results.some(r => r.type === "error");
  const hasWarnings = results.some(r => r.type === "warning");
  const fixableResults = results.filter(r => r.fixId && onQueryChange);

  return (
    <div className="space-y-2 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium">{t("validator.title")}</span>
          {hasErrors && (
            <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
              {results.filter(r => r.type === "error").length} {t("validator.errors")}
            </span>
          )}
          {hasWarnings && (
            <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
              {results.filter(r => r.type === "warning").length} {t("validator.warnings")}
            </span>
          )}
        </div>
        {fixableResults.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-primary hover:text-primary hover:bg-primary/10"
            onClick={() => {
              let fixedQuery = query;
              fixableResults.forEach(result => {
                if (result.fixId && FIX_FUNCTIONS[result.fixId]) {
                  fixedQuery = FIX_FUNCTIONS[result.fixId](fixedQuery);
                }
              });
              onQueryChange?.(fixedQuery);
            }}
          >
            <Wand2 className="w-3 h-3 mr-1" />
            {t("validator.fix_all")} ({fixableResults.length})
          </Button>
        )}
      </div>
      <div className="space-y-1.5">
        {results.map((result, index) => (
          <div
            key={index}
            className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm ${getBgColor(result.type)}`}
          >
            <div className="flex items-start gap-2 flex-1 min-w-0">
              {getIcon(result.type)}
              <span className="text-foreground/90">{t(result.messageKey, result.messageVars)}</span>
            </div>
            {result.fixId && onQueryChange && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs shrink-0 hover:bg-white/10"
                onClick={() => handleFix(result.fixId!)}
              >
                <Wand2 className="w-3 h-3 mr-1" />
                {t("validator.fix")}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
